import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { ConnectorDialog } from "~/ui-rtk/components/features/connectors"
import { confirmationDialogPropsMap } from "~/ui-rtk/components/features/connectors/ConnectorsTable/utils"
import { Button } from "~/ui-rtk/components/ui/controls"
import { ConnectorDto } from "~/ui-rtk/api/types"
import { useCustomConnectorAdditionalConfig } from "~/ui-rtk/hooks/useCustomConnectorAdditionalConfig"
import CustomConfigField from "./CustomConfigField"

interface CustomConnectorConfigFormProps {
  companyConnectorId: string
  connector: ConnectorDto
  isOpen: boolean
  onClose: () => void
}

/**
 * Dialog form for custom (non-Airbyte) connectors that need post-OAuth input
 * (e.g. Instagram → pick which IG Business Account to bind). Schema comes
 * from the connector's service class; option lists are resolved live from
 * the backend per the field's `options_source`.
 */
const CustomConnectorConfigForm: React.FC<CustomConnectorConfigFormProps> = ({
  companyConnectorId,
  connector,
  isOpen,
  onClose,
}) => {
  const {
    schema,
    existingMetadata,
    isLoadingProperties,
    completeSetup,
    isCompletingSetup,
  } = useCustomConnectorAdditionalConfig(companyConnectorId, connector)

  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string[]>([])

  // Seed form data from existing metadata when properties arrive.
  useEffect(() => {
    if (!schema.length || !existingMetadata) return
    setFormData(prev => {
      const next = { ...prev }
      for (const field of schema) {
        const key = field.configuration_path.split(".").pop()
        if (!key) continue
        if (next[key] !== undefined) continue
        const saved = (existingMetadata as Record<string, unknown>)[key]
        if (saved !== undefined && saved !== null) {
          next[key] = saved
        }
      }
      return next
    })
  }, [schema, existingMetadata])

  const isFormValid = useMemo(
    () =>
      schema.every(field => {
        const key = field.configuration_path.split(".").pop()
        if (!key) return false
        const value = formData[key]
        return value !== undefined && value !== null && value !== ""
      }),
    [schema, formData],
  )

  const handleSubmit = async () => {
    setErrorMessage(null)
    setErrorDetails([])
    if (!isFormValid) {
      toast.error("Please fill in all required fields", {
        toastId: "custom-config-missing-required",
      })
      return
    }
    try {
      await completeSetup(formData)
      onClose()
    } catch (err) {
      // Structured validation errors (e.g. IG account scope mismatch from
      // completeCustomConnectorSetup) carry actionable detail in `errors[]`
      // — surface it inline so the user can see which accounts ARE
      // accessible and what to do about it, not just that something failed.
      const data = (err as { data?: { message?: string; errors?: unknown } })
        ?.data
      setErrorMessage(data?.message ?? "Failed to save configuration")
      setErrorDetails(
        Array.isArray(data?.errors)
          ? data.errors.filter((e): e is string => typeof e === "string")
          : [],
      )
    }
  }

  const dialogProps = confirmationDialogPropsMap[connector.id]

  return (
    <ConnectorDialog
      imgSrc={dialogProps?.imgSrc || connector.icon}
      alt={dialogProps?.alt || `${connector.name} Logo`}
      title={dialogProps?.title || `Connection to ${connector.name}`}
      isOpen={isOpen}
      onClose={onClose}
    >
      {isLoadingProperties && (
        <p className="leading-5 text-left text-base text-platinum">
          Loading configuration...
        </p>
      )}

      {!isLoadingProperties && schema.length === 0 && (
        <>
          <p className="leading-5 text-left text-base text-platinum">
            This connector doesn&apos;t require additional configuration.
          </p>
          <Button
            variant={{ variant: "solid", color: "white" }}
            onClick={onClose}
            className="w-full"
          >
            Close
          </Button>
        </>
      )}

      {!isLoadingProperties && schema.length > 0 && (
        <>
          <p className="leading-5 text-left text-base text-platinum">
            Complete the setup for {connector.name} by providing the required
            configuration.
          </p>

          <div className="flex flex-col w-full gap-4 mt-8">
            <div className="space-y-6">
              {schema.map(field => (
                <CustomConfigField
                  key={field.configuration_path}
                  companyConnectorId={companyConnectorId}
                  field={field}
                  autoSelectFirst
                  value={
                    formData[
                      field.configuration_path.split(".").pop() || ""
                    ] as string | undefined
                  }
                  onChange={value => {
                    const key = field.configuration_path.split(".").pop()
                    if (!key) return
                    setFormData(prev => ({ ...prev, [key]: value }))
                  }}
                />
              ))}
            </div>

            {errorMessage && (
              <div className="text-sm text-system-error-400 space-y-1">
                <p className="font-medium">{errorMessage}</p>
                {errorDetails.length > 0 && (
                  <ul className="list-disc pl-5 space-y-1 font-normal text-system-error-300">
                    {errorDetails.map(detail => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <Button
              variant={{ variant: "solid", color: "white" }}
              onClick={() => void handleSubmit()}
              isLoading={isCompletingSetup}
              disabled={isCompletingSetup || !isFormValid}
              className="w-full"
            >
              Complete Setup
            </Button>
          </div>
        </>
      )}
    </ConnectorDialog>
  )
}

export default CustomConnectorConfigForm
